import React, { useEffect, useState, useRef } from "react";
import {
  CircularProgress,
  DialogContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
  Switch, // Import Switch component
  FormGroup,
  FormControlLabel,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import { useRouteMatch } from "react-router-dom";
import queryString from "query-string";
import _ from "lodash";

import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import {
  fetchGlobalDepartmentInfo,
  fetchGlobalGroupInfo,
  fetchProductWrapperInfo,
} from "../../../../services/menuApp/printerMenuService";
import CategoryMenuDropDown from "./CategoryMenuDropDown";
import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import ProductWrapper from "./ProductWrapper";
import { CustomTheme } from "../../../../types/customTheme";
import SwitchCommon from "../../../../components/switches/SwitchCommon";

// Custom hook for data fetching
const useMenuData = (locationId: any) => {
  const [data, setData] = useState({ menuNodes: [], departments: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [groupRes, departmentRes, productRes] = await Promise.all([
          fetchGlobalGroupInfo(locationId),
          fetchGlobalDepartmentInfo(locationId),
          fetchProductWrapperInfo(locationId),
        ]);

        const menuNodes: any = handleMenuRestructure(
          groupRes.data.data,
          departmentRes.data.data,
          productRes.data.data,
        );

        if (menuNodes.length > 0) {
          setData({
            menuNodes,
            departments: menuNodes[0].department,
          });
        }
      } catch (err) {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [locationId]);

  return { data, isLoading, error };
};

const useStyles = makeStyles<CustomTheme>((theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(1.5),
    },
    categoryTitle: {
      backgroundColor: theme.palette.background.entity_highlight_background,
      position: "sticky",
      zIndex: 1,
      fontSize: "20px",
      top: "-0.5px",
      [theme.breakpoints.up("sm")]: {
        fontSize: "32px",
      },
      [theme.breakpoints.up("1280")]: {
        fontSize: "32px",
      },
    },
    categoryTitles: {
      backgroundColor: theme.palette.background.paper,
      position: "sticky",
      zIndex: 50,
      fontSize: "20px",
      top: "-0.5px",
      [theme.breakpoints.up("sm")]: {
        fontSize: "32px",
      },
      [theme.breakpoints.up("1280")]: {
        fontSize: "32px",
      },
    },
  }),
);

export interface StrategyAddEditModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleSelectProduct: any;
  selectedWrapperIdList: string[];
  setSelectedWrapperIdList: (ids: string[]) => void;
  handleSubmitRewardsTypePoints: (
    ids: string[],
    isEdit: boolean,
    rewardId: string | null,
  ) => void;
  isEdit: boolean;
  rewardId: string | null;
  isLoadingButton: boolean;
  isOwner: boolean;
  setIsEditPoint: (isEditing: boolean) => void;
  setIsEditDetails: (isEditing: boolean) => void;
  editableRewardDetails: any;
}

const PointsModalContent: React.FunctionComponent<
  StrategyAddEditModalProps
> = ({
  isOpen,
  setIsOpen,
  handleSelectProduct,
  selectedWrapperIdList,
  handleSubmitRewardsTypePoints,
  isEdit,
  rewardId,
  isLoadingButton,
  isOwner,
  setIsEditPoint,
  setIsEditDetails,
  editableRewardDetails,
  setSelectedWrapperIdList,
}) => {
  const [selectedCatMenuTitle, setSelectedCatMenuTitle] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [removedLinkedIdLists, setRemovedLinkedIdLists] = useState<any>({});
  const [revertExpand, setRevertExpand] = useState("");
  const [isColorChange, setIsColorChange] = useState("");
  // New state for the toggle filter
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const match = useRouteMatch();
  const locationId = (match.params as any).locationId;
  const { data, isLoading, error } = useMenuData(locationId);
  const menuNodes: any = data.menuNodes;
  const departmentList = data.departments;
  const elementsByTitle: any = useRef({});

  useEffect(() => {
    if (menuNodes.length > 0) {
      setSelectedCatMenuTitle(menuNodes[0].name);
    }
  }, [menuNodes]);

  const handleChangeCatMenu = (event: any) => {
    setSelectedCatMenuTitle(event.name);
    const filteredDepartment: any = menuNodes.find(
      (d: any) => d.id === event.id,
    );
    if (filteredDepartment) {
      data.departments = filteredDepartment.department;
    }
  };

  const handleClearStrip = (product: any) => {
    const newRemovedLinkedIds = { ...removedLinkedIdLists };
    delete newRemovedLinkedIds[product.id];
    setRemovedLinkedIdLists(newRemovedLinkedIds);
  };

  const handleClickLinkedId = (item: any, category: any) => {
    setRevertExpand(item.id);
    const newRemovedLinkedIds = { ...removedLinkedIdLists };
    const key = item.directoryLevel === 1 ? item.id : category.directoryId;
    newRemovedLinkedIds[key] = {
      id: item.id,
      directoryLevel: item.directoryLevel,
    };
    setRemovedLinkedIdLists(newRemovedLinkedIds);
  };

  const handleUpdateIdList = (event: any, product: any) => {
    if (event) event.stopPropagation();
    setRemovedLinkedIdLists((prev: any) => {
      const { [product.directoryId]: removed, ...rest } = prev;
      return rest;
    });
    setIsColorChange(product.directoryId);
  };

  const handleNavigate = (product: any, level: any, data: any) => {
    setRemovedLinkedIdLists((prev: any) => ({
      ...prev,
      [data.directoryId]: {
        ...prev[data.directoryId],
        id: product.id,
        directoryLevel: level,
      },
    }));
  };

  const handleButtonClickMobile = (title: any) => {
    const targetElement = elementsByTitle.current[title];
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => setAnchorEl(event.currentTarget);
  const open = Boolean(anchorEl);
  const theme = useTheme<CustomTheme>();
  const classes = useStyles();
  const maxWidthCustom = useMediaQuery(theme.breakpoints.down("1280"));

  // Function to filter departments and products
  const filterDepartments = () => {
    if (!showSelectedOnly) {
      return departmentList;
    }

    const filteredDepartments = departmentList
      .map((department: any) => {
        // Recursively filter products within the department
        const filterProducts = (products: any) => {
          return products.reduce((acc: any[], product: any) => {
            if (selectedWrapperIdList[product.id]) {
              acc.push(product);
            } else if (product.wrapper && product.wrapper.length > 0) {
              const filteredWrapper = filterProducts(product.wrapper);
              if (filteredWrapper.length > 0) {
                acc.push({ ...product, wrapper: filteredWrapper });
              }
            }
            return acc;
          }, []);
        };

        const filteredProducts = filterProducts(department.product);
        return {
          ...department,
          product: filteredProducts,
        };
      })
      .filter((department: any) => department.product.length > 0);

    return filteredDepartments;
  };

  const filteredDepartmentList = filterDepartments();

  return (
    <DialogCommonDefault
      open={isOpen}
      setOpen={setIsOpen}
      isNeedFixedHeight
      maxWidth="xl"
    >
      {isLoading ? (
        <div  style={{ marginTop: "14%", display: "flex", justifyContent: "center" }}>
          <CircularProgress color="secondary" disableShrink />
        </div>
      ) : error ? (
        <div style={{ marginTop: "16%", display: "flex", justifyContent: "center" }}>
          <Typography variant="h3">{error}</Typography>
        </div>
      ) : (
        <>
          <div
            style={{
              position: "fixed",
              top: 32,
              paddingTop: 20,
              zIndex: 1000,
              right: 50,
              left: 57,
              backgroundColor: "transparent",
            }}
          >
            <div style={{ marginRight: 28 }}>
              <Typography variant="h4">
                {isEdit ? "Edit rewards" : "Add rewards"}
              </Typography>
              <Grid container spacing={2} className={classes.root}>
                <Grid item xs={12}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {!_.isEmpty(menuNodes) && selectedCatMenuTitle && (
                        <CategoryMenuDropDown
                          categoryMenuList={menuNodes}
                          selectedCatMenuTitle={selectedCatMenuTitle}
                          handleChangeCatMenu={handleChangeCatMenu}
                        />
                      )}
                      {!_.isEmpty(menuNodes) && departmentList && (
                        <>
                          <IconButton
                            id="basic-button"
                            style={{
                              fontSize: 11,
                              marginLeft: 12,
                              backgroundColor:
                                theme.palette.background.entity_background,
                            }}
                            onClick={handleClick}
                          >
                            {open ? <CloseIcon /> : <MenuIcon />}
                          </IconButton>
                          <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                              "aria-labelledby": "basic-button",
                            }}
                            PaperProps={{
                              elevation: 0,
                              style: {
                                borderRadius: 10,
                                border: `1px solid ${theme.palette.background.entity_border}`,
                                marginTop: 60,
                                marginLeft: 40,
                                top: 320,
                                left: 70,
                                bottom: 34,
                              },
                            }}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            {departmentList.map((department: any) => (
                              <MenuItem
                                key={department.id}
                                onClick={() => {
                                  handleButtonClickMobile(
                                    `department ${department.id}`,
                                  );
                                  handleClose();
                                }}
                                style={{
                                  textTransform: "none",
                                  display: "flex",
                                  justifyContent: "start",
                                }}
                              >
                                <Typography>{department.name}</Typography>
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}
                    </div>
                    {/* New toggle filter */}

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        style={{ marginRight: "0px" }}
                        control={<SwitchCommon />}
                        checked={showSelectedOnly}
                        onChange={(e: any) =>
                          setShowSelectedOnly(e.target.checked)
                        }
                        label="Show only selected"
                        labelPlacement="start"
                      />
                      <ButtonCommon
                        variant="contained"
                        style={{
                          fontSize: 11,
                          width: 100,
                          marginRight: 8,
                          height: 32,
                        }}
                        disabled={isLoadingButton}
                        color="yellow"
                        onClick={() => {
                          setIsOpen(false);
                          setIsEditPoint(false);
                          setIsEditDetails(false);
                        }}
                      >
                        Cancel
                      </ButtonCommon>
                      <ButtonCommon
                        variant="contained"
                        style={{
                          fontSize: 11,
                          marginLeft: 4,
                          width: 100,
                          height: 32,
                        }}
                        isLoadingPage={isLoadingButton}
                        disabled={isLoadingButton}
                        color={isEdit ? "orange" : "green"}
                        onClick={() =>
                          handleSubmitRewardsTypePoints(
                            selectedWrapperIdList,
                            isEdit,
                            rewardId,
                          )
                        }
                      >
                        {isEdit ? "Save" : "Create"}
                      </ButtonCommon>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>
          <DialogContent style={{ padding: "0px 24px", marginTop: "160px" }}>
            {filteredDepartmentList.length > 0 ? (
              filteredDepartmentList.map((category: any, index) => (
                <div key={category.id}>
                  <Typography
                    align="left"
                    className={
                      menuNodes.length
                        ? classes.categoryTitles
                        : classes.categoryTitle
                    }
                    ref={(ref) =>
                      (elementsByTitle.current[`department ${category.id}`] =
                        ref)
                    }
                  >
                    {category.name}
                  </Typography>
                  <Grid
                    container
                    style={{
                      marginTop: index === 0 && maxWidthCustom ? "12px" : "0px",
                    }}
                  >
                    <ProductWrapper
                      wrapperProduct={category.product}
                      removedLinkedIdList={Object.keys(removedLinkedIdLists)}
                      removedLinkedIdLists={removedLinkedIdLists}
                      handleUpdateIdList={handleUpdateIdList}
                      index={index}
                      isColorChange={isColorChange}
                      revertExpand={revertExpand}
                      category={category}
                      handleClickLinkedId={handleClickLinkedId}
                      handleNavigate={handleNavigate}
                      handleClearStrip={handleClearStrip}
                      handleSelectProduct={handleSelectProduct}
                      selectedWrapperIdList={selectedWrapperIdList}
                      setSelectedWrapperIdList={setSelectedWrapperIdList}
                      isOwner={isOwner}
                      editableRewardDetails={editableRewardDetails}
                    />
                  </Grid>
                </div>
              ))
            ) : (
              <div
                style={{ width: "100%", marginTop: "16%", display: "flex", justifyContent: "center" }}
              >
                <Typography variant="h3">No Menus Available.</Typography>
              </div>
            )}
          </DialogContent>
        </>
      )}
    </DialogCommonDefault>
  );
};

export default PointsModalContent;

// Helper function refactored for readability and efficiency
const convertInputToOutput = (productWrapper: any) => {
  const directories: any = {};
  productWrapper.forEach((item: any) => {
    const directoryId = item.directoryId;
    directories[directoryId] = directories[directoryId] || [];
    directories[directoryId].push(item);
  });

  const buildWrapper = (directoryId: any) => {
    const items = directories[directoryId] || [];
    return items.map((item: any) => {
      const wrapper = buildWrapper(item.id);
      return wrapper.length > 0 ? { ...item, wrapper } : item;
    });
  };

  return buildWrapper(-99);
};

// Main data restructuring function
const handleMenuRestructure = (
  groupNode: any,
  departmentNode: any,
  productNode: any,
) => {
  if (!groupNode || !departmentNode || !productNode) return [];

  const sortedGroups = [...groupNode].sort((a, b) => a.viewOrder - b.viewOrder);
  const sortedDepartments = [...departmentNode].sort(
    (a, b) => a.viewOrder - b.viewOrder,
  );
  const sortedProducts = [...productNode].sort(
    (a, b) => a.viewOrder - b.viewOrder,
  );
  const output: any = [];

  const departmentMap = sortedDepartments.reduce((acc, dep) => {
    acc[dep.groupId] = acc[dep.groupId] || [];
    if (
      dep.id > 0 &&
      !["Online Hidden", "Templates", "Hidden Food", "Hidden Drinks"].includes(
        dep.name,
      )
    ) {
      acc[dep.groupId].push({ ...dep, product: [] });
    }
    return acc;
  }, {});

  const productMap = sortedProducts.reduce((acc, prod) => {
    acc[prod.categoryId] = acc[prod.categoryId] || [];
    if (prod.id > 0) {
      acc[prod.categoryId].push(prod);
    }
    return acc;
  }, {});

  sortedGroups.forEach((groupItem) => {
    if (groupItem.id <= 0) return;

    const groupDepartments = departmentMap[groupItem.id] || [];
    groupDepartments.forEach((department: any) => {
      const products = productMap[department.id] || [];
      if (products.length > 0) {
        department.product = convertInputToOutput(products);
      }
    });

    const populatedDepartments = groupDepartments.filter(
      (department: any) => department.product.length > 0,
    );

    if (populatedDepartments.length > 0) {
      output.push({ ...groupItem, department: populatedDepartments });
    }
  });

  return output;
};
